// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// The intake conversation controller. Owns the single, mode-agnostic conversation:
// the unified message list, the live voice phase, and the routing decisions at the end
// of a turn. It delegates the live audio to a swappable VoiceTransport (Vapi today) and
// text turns to the backend SSE channel — the UI depends only on what this hook returns.
// Emergency/complete is backend-authoritative: on call-end we poll status and route; the
// client keyword check is an instant safety net on top.

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '@/i18n/use-language'
import { getConversationId, getPatientFirstName, clearSession } from '@/lib/session'
import { isKioskSession } from '@/lib/kiosk-mode'
import { isAuthError } from '@/lib/api'
import type {
  IntakeLocale,
  IntakeMessage,
  IntakeVisit,
  VoiceCredentials,
  VoiceState,
  VoiceTransport,
} from './types'
import { createVoiceTransport } from './voice/create-voice-transport'
import { getIntakeStatus, getMessages, startIntake } from './intake-service'
import { sendTextTurn } from './text/text-channel'
import { EMERGENCY_INTERCEPT_ENABLED, containsEmergency, formatTime, newMessageId } from './util'

export type IntakePhase = 'initializing' | 'ready' | 'error'

export interface UseIntakeConversation {
  phase: IntakePhase
  messages: IntakeMessage[]
  voiceState: VoiceState
  liveTranscript: string
  liveCaption: string
  permissionDenied: boolean
  isSending: boolean
  muted: boolean
  voiceSupported: boolean
  patientName: string
  /** Appointment context from /intake/start, if the backend had any. Null otherwise. */
  visit: IntakeVisit | null
  /** True once the backend reports the intake complete (text terminal / voice-end status). */
  isComplete: boolean
  /** True when a voice call dropped before completing — drives the in-thread recovery banner. */
  voiceDropped: boolean
  /** Dismiss the voice-dropped recovery banner. */
  dismissVoiceDropped: () => void
  /** Go to the prep-note review. Enabled once complete. */
  reviewSummary: () => void
  /** Show the pre-mic explainer. */
  requestVoice: () => void
  /** User granted the explainer → connect the call (triggers the browser mic prompt). */
  confirmVoice: () => void
  /** Dismiss the explainer, stay in text. */
  cancelVoice: () => void
  /** End the call and route on backend status. */
  endVoice: () => void
  toggleMute: () => void
  /** Leave voice for text without ending the conversation. */
  switchToText: () => void
  sendText: (text: string) => void
  retryInit: () => void
}

const ACTIVE_VOICE_STATES: VoiceState[] = [
  'connecting',
  'reconnecting',
  'patient-listening',
  'assistant-thinking',
  'assistant-speaking',
]

const VOICE_RECONNECT_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 15_000]

export function useIntakeConversation(): UseIntakeConversation {
  const navigate = useNavigate()
  const { current, setLanguage } = useLanguage()
  const locale = current.code as IntakeLocale

  const [phase, setPhase] = useState<IntakePhase>('initializing')
  const [messages, setMessages] = useState<IntakeMessage[]>([])
  const [voiceState, setVoiceStateRaw] = useState<VoiceState>('idle')
  const [liveTranscript, setLiveTranscript] = useState('')
  const [liveCaption, setLiveCaption] = useState('')
  const [permissionDenied, setPermissionDenied] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [muted, setMuted] = useState(false)
  // Voice is available only when the browser supports it AND the backend enabled it
  // (Vapi credential minted server-side; start may return voiceEnabled:false / no vapi block).
  const [voiceEnabled, setVoiceEnabled] = useState(false)
  const [visit, setVisit] = useState<IntakeVisit | null>(null)
  // Backend-authoritative completion. Drives the clean "done" moment + review CTA — NOT a
  // message-count heuristic, and NOT the spoken/streamed farewell wording.
  const [isComplete, setIsComplete] = useState(false)
  // An unexpected call-end before completion (timeout / network / ejection). Distinct from a
  // deliberate End — it surfaces the calm in-thread recovery banner rather than a dead end.
  const [voiceDropped, setVoiceDropped] = useState(false)

  const transportRef = useRef<VoiceTransport | null>(null)
  if (transportRef.current === null) transportRef.current = createVoiceTransport()
  const voiceSupportedByBrowser = transportRef.current.isSupported()

  const credsRef = useRef<VoiceCredentials | null>(null)
  const voiceStateRef = useRef<VoiceState>('idle')
  const intentRef = useRef<'none' | 'switch' | 'end'>('none')
  const cancelTextRef = useRef<(() => void) | null>(null)
  const streamMsgIdRef = useRef<string | null>(null)
  const streamSeqRef = useRef<number | undefined>(undefined)
  const initTokenRef = useRef(0)
  const finishTokenRef = useRef(0)
  const reconnectTokenRef = useRef(0)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const setVoiceState = useCallback((next: VoiceState) => {
    voiceStateRef.current = next
    setVoiceStateRaw(next)
  }, [])

  const isVoiceActive = useCallback(() => ACTIVE_VOICE_STATES.includes(voiceStateRef.current), [])

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
  }, [])

  const cancelReconnect = useCallback(() => {
    reconnectTokenRef.current++
    reconnectAttemptsRef.current = 0
    clearReconnectTimer()
  }, [clearReconnectTimer])

  // Belt-and-suspenders: the server ends the Vapi call on completion (endCall). If that
  // signal is missed, stop the live call client-side so it never hangs open. The 'end'
  // intent guard means we never double-end a call already being torn down.
  const stopVoiceIfActive = useCallback(() => {
    if (isVoiceActive() && intentRef.current !== 'end') {
      intentRef.current = 'end'
      transportRef.current?.stop()
    }
  }, [isVoiceActive])

  const scheduleVoiceReconnect = useCallback(() => {
    const creds = credsRef.current
    const transport = transportRef.current
    if (!creds || !transport?.isSupported()) {
      setVoiceDropped(true)
      setVoiceState('idle')
      return
    }

    const attempt = reconnectAttemptsRef.current
    if (attempt >= VOICE_RECONNECT_DELAYS_MS.length) {
      reconnectAttemptsRef.current = 0
      setVoiceDropped(true)
      setVoiceState('idle')
      return
    }

    const token = ++reconnectTokenRef.current
    const delay = VOICE_RECONNECT_DELAYS_MS[attempt]
    reconnectAttemptsRef.current = attempt + 1
    clearReconnectTimer()
    setLiveCaption('')
    setLiveTranscript('')
    setVoiceDropped(false)
    setVoiceState('reconnecting')

    reconnectTimerRef.current = setTimeout(() => {
      if (token !== reconnectTokenRef.current || intentRef.current !== 'none') return
      void connectVoice(true)
    }, delay)
  }, [clearReconnectTimer, setVoiceState])

  // ── Route at end-of-conversation — backend status is authoritative ────────────
  // Distinguishes a NORMAL completion (engine reached its terminal phase → status
  // 'completed') from an ABNORMAL end (timeout / network / ejection, status still
  // 'active'). Only a completed intake has a prep note, so we route to /prep-note
  // only then. `deliberate` = the patient tapped End; an unexpected drop instead
  // raises the calm recovery banner. Either way we NEVER leave the orb overlay stuck.
  const finish = useCallback(
    async (deliberate: boolean) => {
      const id = getConversationId()
      if (!id) {
        setVoiceState('idle')
        return
      }
      // Guard against a stale finish() from a previous (dropped) call clobbering the
      // state of a freshly restarted one — the last finish wins.
      const token = ++finishTokenRef.current
      const superseded = () => token !== finishTokenRef.current
      try {
        let st = await getIntakeStatus(id)
        if (superseded()) return
        if (EMERGENCY_INTERCEPT_ENABLED && st.status === 'emergency') {
          navigate('/emergency')
          return
        }
        // The interview reached its terminal turn. status flips to 'completed' only
        // after async prep-note synthesis (~15-20s), so a completed call must NOT be
        // treated as a drop. Kiosk submits server-side, so go straight to the done
        // screen; remote waits for the note so it can show the review + Submit.
        if (st.intakeComplete) {
          setIsComplete(true)
          stopVoiceIfActive()
          if (isKioskSession()) {
            navigate('/kiosk/done')
            return
          }
          const deadline = Date.now() + 45_000
          while (st.status !== 'completed' && Date.now() < deadline) {
            await new Promise(r => setTimeout(r, 2_000))
            if (superseded()) return
            st = await getIntakeStatus(id)
            if (superseded()) return
          }
          if (st.prepNoteReady) navigate('/prep-note')
          else setVoiceState('idle')
          return
        }
        if (st.status === 'completed') {
          setIsComplete(true)
          stopVoiceIfActive()
          if (isKioskSession()) navigate('/kiosk/done')
          else if (st.prepNoteReady) navigate('/prep-note')
          else setVoiceState('idle')
          return
        }
        // Genuinely ended before the interview completed: close the overlay and return
        // to the conversation. Unexpected drops try a fresh Vapi call for the same
        // backend conversation before falling back to the recovery banner.
        if (!deliberate) {
          scheduleVoiceReconnect()
          return
        }
        setVoiceState('idle')
      } catch {
        if (superseded()) return
        // If the status poll itself hit the same network blip, try reconnecting.
        if (!deliberate) {
          scheduleVoiceReconnect()
          return
        }
        setVoiceState('idle')
      }
    },
    [navigate, scheduleVoiceReconnect, setVoiceState, stopVoiceIfActive],
  )

  // Repaint the transcript from the CANONICAL server record (GET /messages) — the
  // persisted per-turn text for both text and voice, not Vapi's fragmented live STT.
  // Called on start/resume (shows the assistant's opening message + repaints) and
  // after each settled turn. Best-effort: a transient failure keeps the current view.
  const refreshTranscript = useCallback(async (id: string) => {
    try {
      setMessages(await getMessages(id))
    } catch {
      // keep whatever's currently rendered
    }
  }, [])

  // ── Initialize the conversation on mount ──────────────────────────────────────
  const init = useCallback(() => {
    const token = ++initTokenRef.current
    setPhase('initializing')
    void (async () => {
      try {
        const res = await startIntake(locale)
        if (token !== initTokenRef.current) return
        // Align the UI to the locale the backend resolved from the patient's record,
        // so the interface matches the greeting + voice language (no-op if unchanged
        // or unsupported). Safe: the mount effect runs init once, so this never
        // re-creates the conversation.
        if (res.locale) setLanguage(res.locale)
        credsRef.current = res.vapi
          ? {
              webToken: res.vapi.webToken,
              assistantId: res.vapi.assistantId,
              assistantOverrides: res.vapi.assistantOverrides,
            }
          : null
        setVoiceEnabled(res.voiceEnabled !== false && !!res.vapi)
        setVisit(res.visit ?? null)
        setPhase('ready')
        // Paint the canonical transcript — on a fresh start this surfaces the
        // assistant's visit-aware opening message; on resume it repaints history.
        void refreshTranscript(res.conversationId)
      } catch (err) {
        if (token !== initTokenRef.current) return
        if (isAuthError(err)) {
          // Session expired/invalid mid-intake; the deeplink token is spent, so end the
          // session and route to the honest terminal instead of a dead-end re-verify.
          clearSession()
          navigate('/link-expired')
          return
        }
        setPhase('error')
      }
    })()
  }, [locale, navigate, setLanguage])

  useEffect(() => {
    init()
    const transport = transportRef.current
    return () => {
      // Invalidate any in-flight init and tear down the live channels.
      initTokenRef.current++
      cancelReconnect()
      cancelTextRef.current?.()
      transport?.stop()
    }
    // Run once on mount; init captures the mount-time locale, which is fixed before /chat.
  }, [])

  // ── Voice controls ────────────────────────────────────────────────────────────
  const requestVoice = useCallback(() => {
    // Ref-based guards so this never goes stale: browser support + a minted credential.
    if (!transportRef.current?.isSupported() || !credsRef.current) return
    setVoiceDropped(false)
    setPermissionDenied(false)
    setVoiceState('requesting-permission')
  }, [setVoiceState])

  const cancelVoice = useCallback(() => {
    setPermissionDenied(false)
    setVoiceState('idle')
  }, [setVoiceState])

  async function connectVoice(isReconnect: boolean) {
    const creds = credsRef.current
    const transport = transportRef.current
    if (!creds || !transport) return

    if (!isReconnect) {
      cancelReconnect()
      setVoiceDropped(false)
      setPermissionDenied(false)
      setVoiceState('connecting')
    } else {
      setVoiceState('reconnecting')
    }
    intentRef.current = 'none'

    // Acquire the mic BEFORE opening the Vapi call. If Vapi starts first, the browser
    // permission prompt (and the patient's delay granting it) run while the call is already
    // live and audio-less — the assistant hits its silence timeout and the meeting ends.
    // Probe getUserMedia, release it immediately (Vapi re-acquires instantly once granted),
    // and only connect on success. Covers both the modal (normal) and direct (kiosk) paths.
    if (!isReconnect && navigator.mediaDevices?.getUserMedia) {
      try {
        const probe = await navigator.mediaDevices.getUserMedia({ audio: true })
        probe.getTracks().forEach((t) => t.stop())
      } catch {
        setPermissionDenied(true)
        setVoiceState('requesting-permission')
        return
      }
    }

    void transport
      .start(creds, {
        onConnected: () => {
          reconnectAttemptsRef.current = 0
          clearReconnectTimer()
          setVoiceDropped(false)
          setVoiceState('patient-listening')
        },
        onAssistantSpeaking: () => {
          setLiveTranscript('')
          setVoiceState('assistant-speaking')
        },
        onListening: () => {
          setLiveCaption('')
          setVoiceState('patient-listening')
        },
        onEnded: () => {
          if (intentRef.current === 'switch') {
            intentRef.current = 'none'
            setVoiceState('idle')
            return
          }
          const deliberate = intentRef.current === 'end'
          intentRef.current = 'none'
          setVoiceState('ended')
          void finish(deliberate)
        },
        onMessage: (message) => {
          // Vapi's finalized STT segment is fragmented/mis-transcribed, so we do NOT
          // persist it — clear the live caption and repaint from the CANONICAL record.
          if (message.role === 'assistant') setLiveCaption('')
          if (message.role === 'user') setLiveTranscript('')
          if (EMERGENCY_INTERCEPT_ENABLED && message.role === 'user' && (message.emergencyFlagged || containsEmergency(message.content))) {
            navigate('/emergency')
            return
          }
          // A finalized user turn means the assistant is now computing its reply — surface
          // "thinking" until speech-start flips us to speaking. Only from listening, so a
          // late user-final never yanks us out of an already-started assistant turn.
          if (message.role === 'user' && voiceStateRef.current === 'patient-listening') {
            setVoiceState('assistant-thinking')
          }
          const cid = getConversationId()
          if (cid) void refreshTranscript(cid)
        },
        onPartial: (role, text) => {
          if (role === 'assistant') setLiveCaption(text)
          else setLiveTranscript(text)
        },
        onError: (err) => {
          if (/permission|denied|not-?allowed|microphone|audio/i.test(err.message)) {
            cancelReconnect()
            setPermissionDenied(true)
            setVoiceState('requesting-permission')
          } else {
            transport.stop()
            setVoiceState('ended')
            void finish(false)
          }
        },
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err)
        if (/permission|denied|not-?allowed|microphone|audio/i.test(msg)) {
          cancelReconnect()
          setPermissionDenied(true)
          setVoiceState('requesting-permission')
        } else {
          void finish(false)
        }
      })
  }

  const confirmVoice = useCallback(async () => {
    await connectVoice(false)
  }, [cancelReconnect, clearReconnectTimer, finish, navigate, setVoiceState, refreshTranscript])

  const endVoice = useCallback(() => {
    cancelReconnect()
    intentRef.current = 'end'
    transportRef.current?.stop()
  }, [cancelReconnect])

  const switchToText = useCallback(() => {
    cancelReconnect()
    intentRef.current = 'switch'
    transportRef.current?.stop()
    setLiveCaption('')
    setLiveTranscript('')
    setVoiceState('idle')
  }, [cancelReconnect, setVoiceState])

  const toggleMute = useCallback(() => {
    const transport = transportRef.current
    if (!transport) return
    const next = !transport.isMuted()
    transport.setMuted(next)
    setMuted(next)
  }, [])

  // ── Text turns ──────────────────────────────────────────────────────────────
  const sendText = useCallback(
    (text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return

      // Any text turn is the patient choosing to keep going — clear the recovery banner.
      setVoiceDropped(false)

      const userMsg: IntakeMessage = {
        id: newMessageId(),
        role: 'user',
        content: trimmed,
        modality: isVoiceActive() ? 'voice' : 'text',
        timestamp: formatTime(),
      }
      setMessages((prev) => [...prev, userMsg])

      if (EMERGENCY_INTERCEPT_ENABLED && containsEmergency(trimmed)) {
        navigate('/emergency')
        return
      }

      // In a live voice call, feed the same call; otherwise use the backend text channel.
      if (isVoiceActive()) {
        transportRef.current?.sendText(trimmed)
        return
      }

      const id = getConversationId()
      if (!id) return

      setIsSending(true)
      streamMsgIdRef.current = null
      streamSeqRef.current = undefined

      cancelTextRef.current = sendTextTurn(id, trimmed, {
        onDelta: (chunk) => {
          // Group deltas by sequenceNumber: same seq → append to the same bubble
          // (a long reply is ONE message); a new seq → a fresh bubble. Lazily create
          // the bubble on the first delta so the typing indicator shows until then.
          // Skip a leading empty delta so we never open a blank bubble.
          if (streamMsgIdRef.current === null && chunk.content === '') return
          setMessages((prev) => {
            const sameBubble = streamMsgIdRef.current !== null && streamSeqRef.current === chunk.sequenceNumber
            if (sameBubble) {
              const cur = streamMsgIdRef.current
              return prev.map((m) =>
                m.id === cur
                  ? { ...m, content: m.content + chunk.content, emergencyFlagged: m.emergencyFlagged || chunk.emergencyFlagged === true }
                  : m,
              )
            }
            const nid = newMessageId()
            streamMsgIdRef.current = nid
            streamSeqRef.current = chunk.sequenceNumber
            return [
              ...prev,
              {
                id: nid,
                role: chunk.role,
                content: chunk.content,
                heading: chunk.heading,
                modality: 'text',
                sequenceNumber: chunk.sequenceNumber,
                timestamp: formatTime(),
                emergencyFlagged: chunk.emergencyFlagged === true,
              },
            ]
          })
        },
        onTerminal: (result) => {
          setIsSending(false)
          cancelTextRef.current = null
          streamMsgIdRef.current = null
          streamSeqRef.current = undefined
          if (EMERGENCY_INTERCEPT_ENABLED && (result.status === 'emergency' || result.emergencyFlagged)) {
            navigate('/emergency')
            return
          }
          // Reconcile the turn against the canonical record either way.
          void refreshTranscript(id)
          // Completion is backend-authoritative (StreamTerminal.status), not the farewell
          // wording. On complete, surface the "done" moment and route once the note exists.
          if (result.status === 'completed') {
            setIsComplete(true)
            stopVoiceIfActive()
            if (isKioskSession()) {
              // Kiosk bypasses the patient review/submit — note auto-submitted server-side,
              // patient hands the tablet back. Remote keeps the /prep-note review below.
              navigate('/kiosk/done')
              return
            }
            if (result.prepNoteReady) navigate('/prep-note')
          }
        },
        onError: () => {
          setIsSending(false)
          cancelTextRef.current = null
          streamMsgIdRef.current = null
          streamSeqRef.current = undefined
        },
      })
    },
    [isVoiceActive, navigate, stopVoiceIfActive],
  )

  const dismissVoiceDropped = useCallback(() => setVoiceDropped(false), [])

  // Remote-only patient review CTA. Kiosk never reaches this — it routes straight to
  // /kiosk/done on completion (note auto-submitted server-side), bypassing review/submit.
  const reviewSummary = useCallback(() => navigate('/prep-note'), [navigate])

  const patientName = getPatientFirstName() ?? ''

  return {
    phase,
    messages,
    voiceState,
    liveTranscript,
    liveCaption,
    permissionDenied,
    isSending,
    muted,
    voiceSupported: voiceSupportedByBrowser && voiceEnabled,
    patientName,
    visit,
    isComplete,
    voiceDropped,
    dismissVoiceDropped,
    reviewSummary,
    requestVoice,
    confirmVoice,
    cancelVoice,
    endVoice,
    toggleMute,
    switchToText,
    sendText,
    retryInit: init,
  }
}
